/**
 * #589: event-drop policy for Sentry, extracted from src/index.js so it is
 * testable and so fetch-failure suppression can be route-scoped.
 *
 * Background (2026-08-26 incident): "Load failed" is WebKit's exact wording
 * for ANY failed fetch ("Failed to fetch" is Chromium's). Listing them in
 * ignoreErrors made every Safari network failure on /register invisible by
 * construction — a real caregiver failure produced zero telemetry and had to
 * be reconstructed from nginx access logs. Fetch noise is still suppressed
 * everywhere else; on the registration flow it always reports.
 */

// GMA-1177 / GMA-1178: link scanners / crawlers close the tab mid-fetch and
// generate the bulk of the /register fetch noise. Dropped by UA everywhere,
// including the critical routes — a scanner is not a user.
export const SCANNER_UA_PATTERNS = [
  /SafeLinks/i,
  /BingPreview/i,
  /facebookexternalhit/i,
  /Slackbot/i,
  /bitlybot/i,
  /HeadlessChrome/i,
  /Googlebot/i,
];

// The browser-native wordings for a failed fetch. Kept as patterns, not
// ignoreErrors strings, so the decision can consider the route.
export const FETCH_FAILURE_PATTERNS = [
  /^(TypeError: )?Load failed$/,
  /^(TypeError: )?Failed to fetch$/,
  /NetworkError when attempting to fetch resource/,
  // extractEventMessage prefixes the error NAME, so every pattern accepts the
  // optional "TypeError: " the Load/Failed shapes already did (review finding).
  /^(TypeError: )?NetworkError\b/,
  /^(TypeError: )?Network request failed$/,
];

// Routes where a failed fetch is a stuck caregiver, never noise. A dead
// registration or account-confirmation call MUST reach Sentry.
export const ALWAYS_REPORT_ROUTES = [
  /^\/register(?:$|[/?#])/,
  /^\/users\/confirm_account(?:$|[/?#])/,
];

export function isFetchFailureMessage(message) {
  if (typeof message !== "string" || message === "") return false;
  return FETCH_FAILURE_PATTERNS.some((re) => re.test(message));
}

export function isAlwaysReportPath(pathname) {
  if (typeof pathname !== "string" || pathname === "") return false;
  return ALWAYS_REPORT_ROUTES.some((re) => re.test(pathname));
}

// Best-effort message extraction: the same failure can arrive as an Error
// hint, a bare event.message, or an exception value.
export function extractEventMessage(event, hint) {
  const fromHint = hint && hint.originalException;
  if (fromHint) {
    if (typeof fromHint === "string") return fromHint;
    if (typeof fromHint.message === "string" && fromHint.message !== "") {
      const name = typeof fromHint.name === "string" ? fromHint.name : "";
      // Match the "TypeError: Load failed" shape ignoreErrors used to see.
      return name && name !== "Error"
        ? `${name}: ${fromHint.message}`
        : fromHint.message;
    }
  }
  if (event && typeof event.message === "string" && event.message !== "") {
    return event.message;
  }
  const value =
    event &&
    event.exception &&
    Array.isArray(event.exception.values) &&
    event.exception.values[0];
  if (value) {
    const type = typeof value.type === "string" ? value.type : "";
    const msg = typeof value.value === "string" ? value.value : "";
    if (type && msg) return `${type}: ${msg}`;
    return msg || type;
  }
  return "";
}

// The invite code IS the registration credential (cortex-be builds the
// emailed link from users.uid). The SDK enriches every event with the page
// URL and navigation breadcrumbs, so on /register those fields carry
// ?invite_code=… into Sentry even when no call site logs it. Scrubbed at
// this boundary for every event, not per call site (review finding on
// #593/#595).
// (?:^|[?&]) — a bare query string ("invite_code=…") has no ? prefix, and
// the boundary keeps suffix params like csrftoken= from over-matching.
// "code" is the one-time confirmation/reset credential (ConfirmAccountPage
// and ResetPasswordPage read it straight off the query string). Redacting a
// benign ?code= elsewhere loses nothing; transmitting a live OTP loses a lot.
const SENSITIVE_PARAM_NAMES = new Set([
  "invite_code",
  "access_token",
  "token",
  "code",
]);

// Decode a query-pair KEY only — percent-encoding in the name
// ("invite%5Fcode") must not bypass the redaction (review finding). The
// value is never decoded: it is replaced wholesale.
function decodedKey(rawKey) {
  try {
    return decodeURIComponent(rawKey).toLowerCase();
  } catch (_err) {
    return rawKey.toLowerCase();
  }
}

function isSensitiveKey(rawKey) {
  return SENSITIVE_PARAM_NAMES.has(decodedKey(rawKey));
}

// One segment (a query or a fragment): pair-wise walk, keys matched decoded.
function scrubParamSegment(query) {
  // Pair-wise walk instead of one regex: keys are matched DECODED, so
  // percent-encoded names cannot slip a credential through.
  return query
    .split("&")
    .map((pair) => {
      const eq = pair.indexOf("=");
      if (eq === -1) return pair;
      const key = pair.slice(0, eq);
      return isSensitiveKey(key) ? `${key}=[redacted]` : pair;
    })
    .join("&");
}

/** Issue #628 — a numeric path segment in this product is a CLIENT ID
 *  (`/clients/27/activities`), and it reaches Sentry by three routes that do
 *  not pass through any reporting helper: the automatic fetch breadcrumb, a
 *  console breadcrumb's message, and `event.request.url`. Templating happens
 *  here so every one of them is covered by construction rather than by each
 *  call site remembering.
 *
 *  Only a PURELY numeric segment is templated. `v1` keeps its digit, and a
 *  slug that merely contains a number stays readable — over-scrubbing would
 *  make the breadcrumb useless for the debugging it exists to serve. */
// Anchored on `/` or end-of-token, and deliberately NOT widened to accept the
// characters a URI may not contain. That widening was written as belt-and-braces
// for the round-38 id leak and a mutation proved it unreachable: with the
// tokenisers bounded by URI_CHARS nothing ever hands this an over-long token, so
// reverting it alone kept every test green. Round 16 made the same call one
// function down for the same reason — an unexercised branch reads as protection
// and is not. The invariant it was guarding now lives where it is enforced.
const templateIdSegments = (path) => path.replace(/\/\d+(?=\/|$)/g, "/:id");

/** Issue #628 — the path is not the only place an identifier travels.
 *  ContactContext requests `/contacts?clientId=42` and pontosenseService does
 *  the same, so the id rides in the QUERY. #602's param scrubber only knows
 *  credential-shaped keys, so a numeric id-valued key passed through it
 *  untouched. Any key ending in `id` whose value is purely numeric is an
 *  identifier here, and the value is replaced while the key is kept — the key
 *  is what makes the breadcrumb useful. */
const redactIdParams = (qs) =>
  qs.replace(/([?&]?)([A-Za-z_][\w-]*id)=(\d+)\b/gi, "$1$2=:id");

export function scrubSensitiveUrl(value) {
  if (typeof value !== "string" || value === "") return value;
  // The fragment is scrubbed INDEPENDENTLY of the query (review finding):
  // OAuth-style flows put credentials after # ("/cb#access_token=…"), which a
  // query-only walk never examines. Split it off first so a value containing
  // '#' cannot smuggle a later parameter out of the query walk either.
  const hashIndex = value.indexOf("#");
  const beforeHash = hashIndex === -1 ? value : value.slice(0, hashIndex);
  const fragment = hashIndex === -1 ? null : value.slice(hashIndex + 1);
  const qIndex = beforeHash.indexOf("?");
  const scrubbedMain =
    qIndex === -1
      ? // Bare query strings ("invite_code=…") scrub when a sensitive key
        // matches; a plain sentence with an = is left alone otherwise.
        templateIdSegments(scrubParamSegment(beforeHash))
      : `${templateIdSegments(beforeHash.slice(0, qIndex))}?${redactIdParams(
          scrubParamSegment(beforeHash.slice(qIndex + 1)),
        )}`;
  return fragment === null
    ? scrubbedMain
    : `${scrubbedMain}#${scrubParamSegment(fragment)}`;
}

// request.query_string may be a string, a {key: value} object, or an array
// of [key, value] tuples (all valid Sentry shapes — review finding).
export function scrubQueryString(qs) {
  if (typeof qs === "string") return scrubSensitiveUrl(qs);
  if (Array.isArray(qs)) {
    return qs.map((entry) =>
      Array.isArray(entry) && entry.length >= 2 && isSensitiveKey(String(entry[0]))
        ? [entry[0], "[redacted]", ...entry.slice(2)]
        : entry,
    );
  }
  if (qs && typeof qs === "object") {
    const out = {};
    for (const [k, v] of Object.entries(qs)) {
      out[k] = isSensitiveKey(k) ? "[redacted]" : v;
    }
    return out;
  }
  return qs;
}

/**
 * A span for the presence endpoint, in any shape browser tracing emits it: the
 * description ("POST /api/v2/user/presence") or one of the url-ish data keys.
 */
/**
 * ONE boundary-aware endpoint match, used by every drop.
 *
 * A bare `includes(PRESENCE_ENDPOINT)` also matched
 * `/api/v2/user/presence-settings` and removed a NEIGHBOURING endpoint's
 * telemetry — over-redaction in exactly the place the scope was meant to be
 * narrow. So the endpoint must be followed by a path boundary: end-of-string,
 * `/`, `?`, `#`, or whitespace. `-` is not one, which is what keeps
 * `presence-settings`.
 *
 * A trailing `/` IS a boundary, so anything NESTED under the presence resource
 * — `/api/v2/user/presence/history` — is also dropped. That is deliberate and
 * it is the half worth stating, because it was previously implied by the code
 * and denied by this comment (round 34 review, which read the mismatch the
 * other way and proposed narrowing the code to match the comment).
 *
 * The two cases fail in opposite directions and are not symmetric:
 *   * a SIBLING (`presence-settings`) is a different resource that merely
 *     shares a prefix — dropping it costs telemetry and buys nothing;
 *   * a NESTED path is a sub-resource OF presence. `/presence/history` and
 *     `/presence/devices` are the shapes such a route would actually take, and
 *     both are presence data.
 *
 * On a PHI-adjacent surface the nested case fails CLOSED. No nested presence
 * route exists in the client today, so this decides how a future one behaves
 * before anyone has to remember to ask. `presenceEndpointBoundary` in
 * `sentryBeforeBreadcrumb.test.js` pins both directions.
 */
const matchesPresenceEndpoint = (value) => {
  if (typeof value !== "string") return false;
  const i = value.indexOf(PRESENCE_ENDPOINT);
  if (i === -1) return false;
  const next = value[i + PRESENCE_ENDPOINT.length];
  return next === undefined || "/?#".includes(next) || /\s/.test(next);
};

const isPresenceSpan = (span) => {
  if (!span) return false;
  if (matchesPresenceEndpoint(span.description)) return true;
  const sd = span.data;
  if (!sd) return false;
  return ["url", "http.url", "url.full"].some((k) => matchesPresenceEndpoint(sd[k]));
};

/**
 * Scrub every URL that appears inside a free-text string.
 *
 * Lifted verbatim out of the console-breadcrumb walk in `scrubEventUrls` (#628)
 * so the exception walk below cannot drift from it. A second copy of the
 * sentence-punctuation handling is exactly how one of them would end up fixed
 * and the other not.
 */
/**
 * Absolute URLs, and RELATIVE api paths.
 *
 * #634 review: the relative half was missing entirely, and it is not
 * hypothetical — `baseApi.js` logs `console.error(\`[API] Request failed for
 * ${path}:\`, …)` with `path` arriving as `/clients/27/activities` from
 * `activitiesService.js`. That becomes a console breadcrumb and rides out with
 * the next event, which is the same mechanism #628 fixed for absolute URLs.
 *
 * A relative path must start with `/` AND A LETTER. That is what keeps `1/27`,
 * `3/4`, `/27` and `27/100` out of it — a bare `/\d+` rule would redact
 * fractions and line numbers, and the near-miss cases pin that.
 *
 * It then needs EITHER two or more segments, OR a query/fragment. Both halves
 * matter, and the second was a #602 hole: the first version required two
 * segments and matched the path only, so `/register?invite_code=…` — a
 * single-segment path whose query IS the registration credential — was not
 * matched at all, and `/clients/27/activities?invite_code=…` had its query left
 * behind. `api/v1` stays unmatched: one segment, no query.
 *
 * The suffix is `[^\s]*` so the whole query and fragment reach
 * scrubSensitiveUrl, which already knows how to redact credential-shaped keys
 * and template id params. Trailing sentence punctuation is split off downstream.
 *
 * The absolute alternative is FIRST and the scanner resumes after the whole
 * match, so a path inside an absolute URL is never visited twice.
 */
const URL_OR_RELATIVE_PATH =
  /(?:https?:\/\/[^\s]+|\/[A-Za-z][\w.-]*(?:(?:\/[\w.-]+)+(?:[?#][^\s]*)?|[?#][^\s]*))/g;

export function scrubUrlsInText(text) {
  if (typeof text !== "string" || text === "") return text;
  return text.replace(URL_OR_RELATIVE_PATH, (u) => {
    // Issue #628: a console message ends the URL with sentence punctuation —
    // "…/users/27: Failed to fetch". The match swallowed the colon, so the
    // segment was `27:`, not purely numeric, and the templater skipped it.
    // Split the punctuation off, scrub, put it back: a human reads this
    // message and the colon belongs to them.
    //
    // #634 review: QUOTES belong in this class too. The matcher is `[^\s]+`, so
    // a closing quote is part of the match — `request to
    // ".../clients/27"` arrived as `27"`, which templateIdSegments skips
    // because the digits are not followed by `/` or end-of-string. The #628
    // colon case was handled, which is exactly why this looked covered. The
    // quote is a delimiter the writer added, never part of the URL, so it goes
    // back on the outside like the other punctuation.
    const m = u.match(/^(.*?)([.,;:!?)\]"'`]*)$/);
    return scrubSensitiveUrl(m[1]) + m[2];
  });
}

/**
 * PR #634 review — an identifier introduced by a LABEL, in prose.
 *
 * A render throw carries no URL, so the whole URL-shaped apparatus above
 * cannot see it: `panel exploded: client 27 activities` left the browser
 * intact. This is the narrowest rule that closes that.
 *
 * DELIBERATELY NOT "redact digits". #633 set the house position that
 * over-scrubbing makes an event useless for the debugging it exists to serve —
 * `templateIdSegments` spares `v1` for the same reason. So a number is an
 * identifier here only when a label says so. `render took 27 ms`, `expected 3
 * panels` and `HTTP 502` keep their numbers, and the near-miss cases in
 * `sentryExceptionTextScrub.test.js` are what hold that line: if they go red,
 * this rule got greedy.
 *
 * The LABEL is kept and only the value replaced, matching `redactIdParams` —
 * the label is what makes the event actionable.
 */
const ID_LABELS = "client|user|patient|record|caregiver";

/**
 * #634 review: the first version required `id` to be GLUED to the label
 * (`clientId`, `client_id`) and the separator to be whitespace, `:` or `=`. So
 * `client ID 27`, `client-id: 27` and `client #27` all passed through, and all
 * three are unambiguously identifiers.
 *
 * - label: the noun, optionally followed by `id` across up to two spaces,
 *   underscores or hyphens — `client`, `clientId`, `client_id`, `client-id`,
 *   `client ID`. Bounded, and using ` \t` rather than `\s` so it cannot span a
 *   newline and glue two unrelated lines together.
 * - separator: `:`/`=`, or `#`, or plain whitespace. Preserved verbatim in the
 *   output so `client #27` reads `client #:id` — the label AND the shape are
 *   what make the event actionable. Bounded to ` \t` for the SAME reason as the
 *   label above: it was `\s`, which includes `\n`, so the newline guard the
 *   label documents was defeated one group over — `failed for client\n27 panels
 *   expected` redacted the panel COUNT, which is not an identifier and is
 *   precisely the diagnostic the event exists to carry.
 *
 * `#` widens this, so the near-miss list gained `issue #27` and `ID mismatch on
 * 3 rows`: the `#` is only an identifier when one of these nouns introduces it.
 */
const LABELLED_ID = new RegExp(
  `\\b((?:${ID_LABELS})(?:[ \\t_-]{0,2}id)?)([ \\t]*[:=][ \\t]*|[ \\t]*#[ \\t]*|[ \\t]+)(\\d+)\\b`,
  "gi",
);

export function redactLabelledIds(text) {
  if (typeof text !== "string" || text === "") return text;
  return text.replace(LABELLED_ID, "$1$2:id");
}

/** Both text rules, in the order that matters: URLs first, so a path
 *  identifier is templated by the URL walk rather than caught piecemeal. */
export function scrubFreeText(text) {
  return redactLabelledIds(scrubUrlsInText(text));
}

export function scrubEventUrls(event) {
  if (!event || typeof event !== "object") return event;
  // PR #634 review — the two fields that carry a THROWN string.
  //
  // `Sentry.ErrorBoundary` captures the original exception, so a panel's render
  // error arrives as `exception.values[].value`. #634 hides that string from the
  // caregiver's screen on the grounds that it can carry a client id; the same
  // reasoning applies to sending it. Measured before this landed: the value went
  // out unchanged while `request.url` beside it was correctly templated — the
  // scrubber worked and simply never walked here.
  //
  // `stacktrace` is untouched on purpose: the frames are what make the event
  // worth keeping, and they carry file/line, not record data.
  if (event.exception && Array.isArray(event.exception.values)) {
    for (const value of event.exception.values) {
      if (value && typeof value.value === "string") {
        value.value = scrubFreeText(value.value);
      }
    }
  }
  // #634 review: `event.message` is a STRING, full stop — `@sentry/core`'s Event
  // type declares `message?: string` and puts parameterised text in
  // `logentry?: { message, params }` (event.d.ts:23-27). An earlier version of
  // this walk handled `event.message` as an object too, which was a shape I
  // invented: it tested something that never arrives while the real `logentry`
  // bypassed the scrub entirely.
  if (typeof event.message === "string") {
    event.message = scrubFreeText(event.message);
  }
  if (event.logentry && typeof event.logentry === "object") {
    // `formatted` is not in this SDK version's type but is part of the wire
    // protocol, so it is walked defensively rather than assumed absent.
    for (const k of ["message", "formatted"]) {
      if (typeof event.logentry[k] === "string") {
        event.logentry[k] = scrubFreeText(event.logentry[k]);
      }
    }
    // `params` holds the INTERPOLATED values — the client id is more likely to
    // be here than in the template. Caveat worth stating: a param that is a
    // bare `"27"` carries no label and no path, so nothing here can identify it
    // as an id. This covers the labelled and path-shaped ones, not that case.
    if (Array.isArray(event.logentry.params)) {
      event.logentry.params = event.logentry.params.map((p) =>
        typeof p === "string" ? scrubFreeText(p) : p,
      );
    }
  }
  if (event.request) {
    if (typeof event.request.url === "string") {
      event.request.url = scrubSensitiveUrl(event.request.url);
    }
    if (event.request.query_string !== undefined) {
      event.request.query_string = scrubQueryString(event.request.query_string);
    }
    if (event.request.headers && typeof event.request.headers === "object") {
      // Header names are case-insensitive (review finding): scrub every
      // spelling of referer, and any header value carrying a URL query.
      for (const key of Object.keys(event.request.headers)) {
        if (key.toLowerCase() === "referer" &&
            typeof event.request.headers[key] === "string") {
          event.request.headers[key] = scrubSensitiveUrl(event.request.headers[key]);
        }
      }
    }
  }
  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) {
      // Issue #628: a console breadcrumb carries the url in its MESSAGE, not
      // in data.url, so the field walk below never saw it. apiRequest's own
      // console.error is exactly this shape. #528 owns the broader
      // console-breadcrumb problem; this covers the URL-shaped part.
      if (crumb && typeof crumb.message === "string") {
        // #634 review: was an inline copy of this walk. Shared with the
        // exception walk above so the two cannot drift apart.
        crumb.message = scrubUrlsInText(crumb.message);
      }
      const d = crumb && crumb.data;
      if (!d) continue;
      // #695: send-time breadcrumbs can still hold class instances if they
      // skipped beforeBreadcrumb (or were attached after). Same redaction as
      // creation time, then URL fields on the result.
      try {
        const isPresence = isPresenceBreadcrumb(crumb, d);
        crumb.data = redactStateInValue(d, 0, isPresence);
      } catch (_ignored) {
        crumb.data = { note: REDACTED };
      }
      const scrubbed = crumb.data;
      if (scrubbed && typeof scrubbed === "object") {
        for (const k of ["url", "to", "from"]) {
          if (typeof scrubbed[k] === "string") {
            scrubbed[k] = scrubSensitiveUrl(scrubbed[k]);
          }
        }
      }
    }
  }
  // Transactions carry spans whose data/description hold fetch + navigation
  // URLs; replay events carry a bare urls[] array. Both bypass beforeSend, so
  // this function is also registered as a GLOBAL event processor and via
  // beforeSendTransaction (review finding).
  if (Array.isArray(event.spans)) {
    // Presence spans are REMOVED, not scrubbed. Tracing runs at a 10%
    // production sample, so a sampled transaction carried timestamped
    // /api/v2/user/presence spans — and scrubbing the url of a span whose
    // EXISTENCE is the signal does nothing. The fourth representation of this
    // one datum, after the success log, the failure log and the automatic fetch
    // breadcrumb. Only this endpoint is dropped; every other span keeps its
    // tracing.
    event.spans = event.spans.filter((span) => !isPresenceSpan(span));
    for (const span of event.spans) {
      if (!span) continue;
      if (typeof span.description === "string") {
        span.description = scrubSensitiveUrl(span.description);
      }
      const sd = span.data;
      if (sd) {
        // v8 browser tracing records the full fetch URL as `url.full`.
        for (const k of ["url", "http.url", "url.full", "server.address"]) {
          if (typeof sd[k] === "string") sd[k] = scrubSensitiveUrl(sd[k]);
        }
      }
    }
  }
  if (Array.isArray(event.urls)) {
    event.urls = event.urls.map((u) => (typeof u === "string" ? scrubSensitiveUrl(u) : u));
  }
  if (typeof event.culprit === "string") {
    event.culprit = scrubSensitiveUrl(event.culprit);
  }
  return event;
}

// The Session Replay RECORDING payload (rrweb events) bypasses event
// processors entirely — e.g. the Meta event's data.href carries the full page
// URL (review finding). Wired via replayIntegration's beforeAddRecordingEvent.
const RECORDING_URL_KEYS = new Set(["href", "url", "to", "from"]);

export function scrubRecordingEvent(recordingEvent) {
  // Iterative walk, no depth cap: rrweb full snapshots nest the whole DOM
  // (childNodes chains) far deeper than any fixed bound, and an href a few
  // elements down carries the same credential (review finding). `seen`
  // guards cycles; a stack avoids recursion limits on huge snapshots.
  if (!recordingEvent || typeof recordingEvent !== "object") {
    return recordingEvent;
  }
  // Replay is a THIRD carrier of the presence cadence, reached by neither
  // beforeSend nor beforeSendTransaction. The SDK turns every fetch into a
  // `performanceSpan` recording event whose `payload.description` is the
  // request URL, so the 30s presence POST was arriving as timestamped replay
  // telemetry — and `description` is not a key this walk scrubs.
  //
  // Scrubbing the URL would not help: the sensitive datum is the EXISTENCE of
  // the event on a fixed cadence, not the path. So the event is dropped, which
  // returning null does (`addEvent` skips a falsy callback result).
  //
  // Endpoint-scoped by the same boundary-aware matcher as the span and
  // breadcrumb drops, so sibling resources — and `/api/v2/user/presence-settings`
  // — are kept. A replay with no network events is not a debuggable session.
  const recordingPayload =
    recordingEvent.data &&
    recordingEvent.data.tag === "performanceSpan" &&
    recordingEvent.data.payload;
  if (recordingPayload && matchesPresenceEndpoint(recordingPayload.description)) {
    return null;
  }
  const seen = new Set();
  const stack = [recordingEvent];
  while (stack.length > 0) {
    const value = stack.pop();
    if (!value || typeof value !== "object" || seen.has(value)) continue;
    seen.add(value);
    if (Array.isArray(value)) {
      for (const v of value) stack.push(v);
      continue;
    }
    for (const [k, v] of Object.entries(value)) {
      if (RECORDING_URL_KEYS.has(k) && typeof v === "string") {
        value[k] = scrubSensitiveUrl(v);
      } else if (v && typeof v === "object") {
        stack.push(v);
      }
    }
  }
  return recordingEvent;
}

/**
 * #528: in-home client state that must never ride out on a breadcrumb.
 *
 * Sentry's default `Breadcrumbs` integration turns every console call into a
 * breadcrumb held on the scope and shipped with the NEXT event. So a
 * `console.log` of presence state is not "just a log" — it is telemetry, and
 * `beforeSend` is the wrong layer to catch it because it only sees breadcrumbs
 * that survived to an event.
 *
 * Keys, not call sites: matching on `[PRESENCE]` would cover today's one
 * logger and miss the next one.
 */
/**
 * Identifiers. These identify a PERSON or a DEVICE, so they are redacted on
 * every breadcrumb regardless of category — an id is identifying wherever it
 * appears.
 */
export const IDENTITY_KEYS = [
  "userId",
  "user_id",
  "remoteUserId",
  // A persistent localStorage UUID: it tracks a browser across sessions.
  "deviceId",
  "device_id",
];

/**
 * Presence VOCABULARY. These describe a client's presence/session state and are
 * redacted only inside a presence-scoped breadcrumb.
 *
 * Scoping matters: applied to every breadcrumb, this list erased telemetry that
 * other features deliberately add — CallV3Context puts `callId` on its
 * `call.lifecycle` breadcrumbs and a test requires it, and this PR was replacing
 * it with [redacted] before Sentry saw it. A privacy filter that blinds an
 * unrelated domain has traded one defect for another.
 */
export const PRESENCE_STATE_KEYS = [
  "callStatus",
  "isOnline",
  "platform",
  "appVersion",
  "batteryLevel",
  "lastSeen",
  "is_online",
  "last_seen",
  "updated_at",
  "call_status",
  "app_version",
  "x_app_version",
  "platform_raw",
  "x_app_platform",
  "device_model",
  "device_info",
  "battery_level",
  "os_version",
  "build_number",
  "x_build_number",
  "updatedAt",
  "appType",
  "buildNumber",
  "deviceInfo",
  "deviceModel",
  "packageName",
  "disconnectReason",
  // The text path redacts every `action=`; without this the structured copy of
  // the same field kept the direction. Ambiguous by design (see the split
  // below), so a bare `action` never infers presence scope on its own.
  "action",
  "callId",
  "channel",
  "callDirection",
  "callType",
  "browserName",
  "browserVersion",
  "connectionType",
  "timezone",
  "osVersion",
];

/** Kept for the text matcher, which runs only on presence-marked messages. */
export const SENSITIVE_STATE_KEYS = [
  ...IDENTITY_KEYS,
  ...PRESENCE_STATE_KEYS,
];

/**
 * #528: the client id in a presence message.
 *
 * A client id is a pseudonymous identifier the fleet already permits in
 * telemetry, so this is not the leak the ticket is about. Templating it anyway
 * costs nothing, matches what `scrubSensitiveUrl` does to `/clients/27/`, and
 * removes the only remaining identifier from the line.
 */
const templateUserId = (text) => {
  if (typeof text !== "string") return text;
  // Three producers, three separators. `useFirebasePresence.js:161` logs
  // `User 20`, `:78` logs `userId: "20"` (colon AND quotes — the line that
  // fires on every profile view), and the error path at `:171` logs
  // `user 27 data`. A whitespace-only pattern caught the first and missed the
  // one that runs most often.
  return text.replace(
    /\b(users?Id|user)(\s*[:=]\s*|\s+)(["']?)\d+\3/gi,
    (_m, key, sep, q) => `${key}${sep}${q}:id${q}`
  );
};

const REDACTED = "[redacted]";

// Every value-redacting text pass must be IDEMPOTENT, because they run in
// sequence over one string. The placeholder ends in `]`, and each value class
// below excludes `]` to stop a match running past the value — so a pass
// reaching an already-redacted value matched `[redacted` and appended a second
// closer, shipping `action=[redacted]]`. Listing the placeholder as the FIRST
// alternative in every value group makes each pass a no-op over its own output
// and over its siblings'. Derived from the constant, so changing the
// placeholder cannot silently reopen this.
const REDACTED_LITERAL = REDACTED.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Three separate guards, and it is worth being exact about which does what —
// an earlier version of this comment credited the wrong one:
//   * the leading `\b` stops a key SUFFIXED by a sensitive name (`subplatform`)
//   * the required `:`/`=` stops a key PREFIXED by one (`platformNotes`)
//   * excluding whitespace from the value stops the match running on into the
//     rest of the sentence
// The match is case-SENSITIVE, so `myPlatform` is already excluded by case and
// exercises none of them.
// Sentry serialises a console argument to JSON, so `{"platform":"apple_watch"}`
// is the shape that actually ships — a matcher accepting only a bare key
// redacts the pretty-printed form and misses the wire form entirely.
//
// Group 1 carries the preceding character and any opening quote, group 3 the
// closing quote and separator, so both are put back verbatim and only the value
// is replaced. Requiring a non-word character before the key is what still
// stops `subplatform`, and requiring the separator immediately after is what
// still stops `platformNotes`.
// TWO matchers, one per scope — mirroring the object walk exactly. The text
// path applied the full key set unconditionally, so the SAME breadcrumb kept
// callId in `data` and redacted it in `message`: the scoping was implemented on
// one representation and not its twin.
// `failClosed` decides ONLY the last resort — a bare value with no explicit
// boundary. Round 36: the narrow form below stops at the first whitespace or
// comma, which is right for a one-token value and fails OPEN for every value
// that is not one token. `platform: Apple Watch Series 6` shipped as
// `platform: [redacted] Watch Series 6`, and `device_model: Watch6,2` left
// `,2`. Nothing lexical separates a multi-token value from a value followed by
// a sentence, so the presence matcher takes the whole line rather than guess.
// The identity matcher keeps the narrow rule: ids are opaque single tokens, so
// there is no multi-token leak there to fail closed against, and eating the
// sentence would cost call-lifecycle telemetry for nothing.
const makeStateKeyMatcher = (keys, { failClosed = false } = {}) => new RegExp(
  `((?:^|[^\\w])["']?)(${keys.join("|")})(["']?\\s*[:=]\\s*)` +
    // Four alternatives, tried IN ORDER, and the order is the whole design:
    //   1. the placeholder itself, so every pass is a no-op over its own output
    //   2. a QUOTED value — the quotes are the boundary, prose after it stays
    //   3. an unquoted value INSIDE an object — the next key or the closing
    //      brace is the boundary, so `Watch6,2` and `Apple Watch Series 6`
    //      survive intact rather than being cut at their own separator
    //   4. a bare value in prose, where the text states no boundary at all —
    //      `failClosed` decides this one (see above)
    // Escape pairs are part of the value: `"Apple \\"Watch\\" X"` stopped at the
    // first escaped quote, leaving the remainder in the breadcrumb.
    `(${REDACTED_LITERAL}` +
      `|"(?:[^"\\\\]|\\\\.)*"|'(?:[^'\\\\]|\\\\.)*'` +
      `|[^}\\n]*?(?=\\s*[,;]\\s*["']?[\\w$]+["']?\\s*[:=]|\\s*[}\\]])` +
      // Last resort: no quotes, no following key, no closing brace — nothing in
      // the text says where the value ends. See `failClosed` above.
      (failClosed ? `|[^\\n]*` : `|[^,}\\]\\s]+`) +
      `)`,
  "g"
);

const STATE_KEY_PRESENCE = makeStateKeyMatcher(SENSITIVE_STATE_KEYS, {
  failClosed: true,
});
const STATE_KEY_IDENTITY_ONLY = makeStateKeyMatcher(IDENTITY_KEYS);

const redactStateInText = (text, isPresence = true) =>
  typeof text === "string"
    ? text.replace(
        isPresence ? STATE_KEY_PRESENCE : STATE_KEY_IDENTITY_ONLY,
        `$1$2$3${REDACTED}`,
      )
    : text;

/**
 * Run the URL scrubber ONLY on text that plausibly holds a URL or an absolute
 * path.
 *
 * Applied to arbitrary prose it rewrites slash-delimited numbers — dates,
 * ratios, version-like diagnostics — so `retry on 08/31/2026` came back as
 * `08/:id/:id`, corrupting diagnostics for no privacy gain.
 *
 * ONE helper, BOTH call sites. These were guarded separately once and the
 * message path was missed — a second copy of the test is how they drift.
 */
/**
 * In a presence breadcrumb the DIRECTION is the sensitive datum, and it is
 * carried by prose rather than by a key.
 *
 * 25 console lines across useOnlineStatus.js and onlineStatusManager.js say it
 * in words — "setting offline", "Browser offline", "ensuring offline state".
 * A key-based redactor cannot see those: there is no key, and the datum is the
 * sentence.
 *
 * So the DIRECTION WORDS go and everything else stays. Redacting the whole body
 * was tried first and an existing guard correctly rejected it: "Status updated"
 * is state-NEUTRAL, and blanking it turns a useful breadcrumb into silence.
 * Signal, not silence — the breadcrumb still says presence changed and for whom
 * (templated), just not which way.
 *
 * Fail-closed for new producers: scoped by the [PRESENCE] marker, so a log added
 * next week is covered with no new key to remember.
 */
const PRESENCE_MARKER = /\[PRESENCE(?:_[A-Z]+)?\]/;
const PRESENCE_DIRECTION =
  /\b(?:online|offline|logout|login|logged[ -]?in|logged[ -]?out|(?:background_)?heartbeat)\b/gi;

// The already-redacted placeholder is an ATOMIC alternative, listed first.
// Without it this pass ran over the direction pass's own output: `login`
// became `[redacted]`, and the value class here excludes `]`, so it matched
// `[redacted` and appended a second closer — `action=[redacted]]`. A text
// transform that is not idempotent corrupts whatever ran before it.
const PRESENCE_ACTION = new RegExp(
  `\\b(action\\s*=\\s*)(?:${REDACTED_LITERAL}|[^\\s,;)}\\]]+)`,
  "gi",
);

// Uses the COMPUTED scope, falling back to the marker. It used to re-detect
// `[PRESENCE]` only, so a breadcrumb proven to be presence by its DATA still
// shipped the direction in its message — the decision was made once and then
// half the pipeline ignored it.
const redactPresenceMessage = (text, isPresence = false) =>
  typeof text === "string" && (isPresence || PRESENCE_MARKER.test(text))
    ? text
        .replace(PRESENCE_DIRECTION, REDACTED)
        // Every action VALUE, not a listed set. The direction list had grown
        // online -> offline -> heartbeat -> app_foreground; an enumeration over
        // a vocabulary we do not own fails open by construction.
        .replace(PRESENCE_ACTION, `$1${REDACTED}`)
    : text;

/**
 * THE text pipeline. Both the message and the data-argument paths call this and
 * nothing else.
 *
 * Five review rounds on this PR were the same defect: a transform added to one
 * of the two paths and not the other, each time invisible because the fixed
 * path's test went green. Extracting `scrubUrlish` was not enough — the next
 * transform (`redactPresenceMessage`) was added at one site again. A single
 * named pipeline is the only shape where "both paths" is not a thing to
 * remember.
 *
 * Anything that transforms breadcrumb TEXT goes in here, never at a call site.
 */
const scrubMessageText = (value, isPresence = true) =>
  redactPresenceMessage(
    templateUserId(
      redactStateInText(redactRawSubtreeInText(scrubUrlish(value)), isPresence),
    ),
    isPresence,
  );

/**
 * Round 16: scrub each PATH TOKEN, not the whole string.
 *
 * scrubSensitiveUrl anchors a trailing id on `/` or end-of-string, so handing
 * it a prose sentence left the id whenever punctuation or a word followed —
 * `GET /clients/27 failed` and `.../users/27: Failed to fetch` both kept the
 * 27, and prose is the shape a console breadcrumb actually has. Extracting the
 * token first gives the scrubber the end-of-string it anchors on.
 */
// The token runs to whitespace, exactly as the absolute-URL one does. A narrow
// path-character class stopped at `?` and `#`, handing the scrubber only the
// path and leaving the credential in the query — while the identical URL with a
// scheme was redacted correctly, since that branch was already greedy.
// The leading boundary is whitespace OR a JSON/quote delimiter. Requiring
// whitespace meant `{"url":"/clients/27"}` never tokenised at all — there is no
// space before the path in serialised console arguments, which is exactly how
// these breadcrumbs arrive. A digit before the slash still does not match, so
// "08/31/2026" is untouched.
// The body runs to WHITESPACE, exactly as ABSOLUTE_URL_TOKEN does — the two
// branches must agree on where a URL ends or the same credential is redacted in
// one representation and shipped in the other.
//
// The comma was already handled this way: legal inside a query value
// (`?next=a,b`), so excluding it truncated the token and left the rest
// unscrubbed. Round 37 found the identical defect on every OTHER delimiter the
// body excluded — `" ' ` ] ) }` — each of them legal in a query too:
//
//   GET /register?next=a)b&invite_code=SECRET     ->  shipped the credential
//   GET https://x/register?next=a)b&invite_code=… ->  redacted it
//
// They are still terminators; deciding where the token ENDS is simply not this
// pattern's job. scrubUrlToken strips them as a trailing tail, which is the one
// place that decision is made and the one the absolute branch already used.
// They remain LEADING boundaries, so `{"url":"/clients/27"}` still tokenises.
// RFC 3986 decides where a token ends, because a flat character class cannot.
// Round 37 widened the body to whitespace to stop a query being truncated at a
// legal delimiter; that introduced the opposite leak — `{"url":"/clients/27",
// "method":"GET"}` tokenised past the closing quote, so the id was no longer at
// a segment boundary and shipped untemplated. A token too SHORT misses the
// query; a token too LONG misses the id. Both leak, so neither direction is a
// safe default.
//
// The spec separates them: `!$&'()*+,;=` are sub-delims and `:/?#[]@` gen-delims,
// all legal in a URI, so the token runs THROUGH them and round 37's fix stands.
// `" ` { } | \ ^ < >` are excluded from URIs altogether, so a token ends there —
// which is precisely why a URL inside a JSON string stops at its closing quote.
//
// Known cost: a MALFORMED url with a raw `"` or `}` in its query (RFC requires
// %22 / %7D) truncates, so a credential after one is missed. Narrower than
// shipping client ids out of the JSON console arguments these breadcrumbs are
// actually made of. Pinned in both directions in the test file.
const URI_CHARS = "A-Za-z0-9\\-._~:/?#\\[\\]@!$&'()*+,;=%";
const PATH_TOKEN = new RegExp(`(^|[\\s"'\`[(,:])(\\/[${URI_CHARS}]*)`, "g");

// Round 21: an ABSOLUTE url in prose needs the same token extraction the
// relative path got. Round 16 fixed the relative case and then routed anything
// containing "http" through the whole-sentence path — which is the very defect
// it was written to close, left standing in the twin representation. Both
// shapes are now extracted before scrubbing, so the scrubber always sees a
// token that ends where the URL ends.
// Same class as PATH_TOKEN — the review found this branch carried the JSON
// defect too, and it always had: `[^\s]*` ran past the closing quote of a
// multi-property object exactly as the widened relative branch did.
const ABSOLUTE_URL_TOKEN = new RegExp(`https?://[${URI_CHARS}]*`, "g");

// ONE tokeniser for both branches. Trailing sentence punctuation is not part of
// a URL, and leaving it attached hides the terminal segment from
// scrubSensitiveUrl's `/`-or-end-of-string anchor. Written once so the two
// branches cannot drift again — they have now diverged twice, in opposite
// directions.
const scrubUrlToken = (url) => {
  // Sentence punctuation AND the quote/JSON closers. The closers were dropped in
  // round 16 because a mutation proved them unreachable — PATH_TOKEN excluded
  // them from the token body, so nothing ever arrived here carrying one. Round
  // 37 removed that exclusion (it was leaking credentials past the truncation),
  // which makes this the only place the closers are handled, and reachable
  // again. The mutation that retired them now kills.
  const m = /["'`\]),.;:!?}]+$/.exec(url);
  const tail = m ? m[0] : "";
  const bare = tail ? url.slice(0, -tail.length) : url;
  return scrubSensitiveUrl(bare) + tail;
};

const scrubUrlish = (value) => {
  if (typeof value !== "string") return value;
  let out = value;
  if (/https?:\/\//.test(out)) {
    out = out.replace(ABSOLUTE_URL_TOKEN, scrubUrlToken);
  }
  // Same boundary as PATH_TOKEN. This gate kept the old whitespace-only form
  // once already, so widening the token alone changed nothing — the early
  // return above the fix.
  if (!/(^|[\s"'`[(,:])\//.test(out)) return out;
  return out.replace(PATH_TOKEN, (m, lead, path) => lead + scrubUrlToken(path));
};

/**
 * Keys whose VALUE is an upstream payload we do not define.
 *
 * The key list below is an allowlist of things known to be sensitive, and an
 * allowlist fails OPEN: every field nobody thought of ships. FirebaseDataUtils
 * alone names 13 fields the list did not carry — callId, remoteUserId, channel,
 * disconnectReason, browserName, connectionType, timezone — and remoteUserId is
 * a SECOND client's identifier riding inside the first client's breadcrumb.
 * A schema we do not own can gain a field next week and nothing here changes.
 *
 * So a raw snapshot is redacted as a UNIT. Fail closed on the payloads whose
 * shape is not ours to enumerate.
 */
const RAW_PAYLOAD_KEYS = new Set([
  "rawData",
  "raw",
  // normalizeUserData stores the whole upstream payload here
  // (FirebaseDataUtils.js:80, commented "Raw data for debugging").
  "_raw",
  "snapshotValue",
]);

/**
 * A raw snapshot arrives in TWO representations. RAW_PAYLOAD_KEYS guards the
 * structured `data.arguments` copy; the console integration also folds the same
 * argument into `breadcrumb.message` as JSON text, where a key set cannot reach
 * it. Same subtree, same unknowable schema — so the text form is redacted too.
 */
const RAW_KEY_IN_TEXT = new RegExp(
  // Leading boundary, preserved in group 1: without it `draw:` matched on its
  // `raw` suffix and this pipeline — which runs on EVERY message — redacted an
  // unrelated chart diagnostic.
  // The value is matched in ANY shape, not just `{`. A Firebase snapshot can be
  // an array or a scalar, and normalizeUserData accepts either — so an
  // object-only matcher applied the fail-closed policy to one shape of the same
  // payload and let the others through in the serialized message.
  `(^|[^\\w])(["']?(?:${[...RAW_PAYLOAD_KEYS].join("|")})["']?\\s*:\\s*)`,
  "g"
);

/**
 * Brace-balanced, because a flat-only pattern fails OPEN: a snapshot carrying a
 * nested `devices` map simply does not match, so the entire payload survives
 * rather than being redacted. Scanning to the matching brace is the only form
 * that nesting we do not control cannot defeat.
 */
const redactRawSubtreeInText = (value) => {
  if (typeof value !== "string") return value;
  let out = "";
  let last = 0;
  RAW_KEY_IN_TEXT.lastIndex = 0;
  let m;
  while ((m = RAW_KEY_IN_TEXT.exec(value)) !== null) {
    const valueAt = m.index + m[0].length;
    const opener = value[valueAt];
    const closer = opener === "{" ? "}" : opener === "[" ? "]" : null;
    if (closer === null) {
      // A SCALAR value (quoted string, number, boolean, null). It has no
      // balanced delimiters, so it ends at the first structural separator
      // outside quotes. Redact it whole — an opaque snapshot scalar is exactly
      // as unknown as an opaque object.
      let sEnd = valueAt;
      let sQuote = null;
      for (; sEnd < value.length; sEnd += 1) {
        const ch = value[sEnd];
        if (sQuote) {
          if (ch === "\\") sEnd += 1;
          else if (ch === sQuote) sQuote = null;
          continue;
        }
        if (ch === '"' || ch === "'") sQuote = ch;
        else if (ch === "," || ch === "}" || ch === "]") break;
      }
      out += value.slice(last, m.index) + m[1] + m[2] + REDACTED;
      last = sEnd;
      RAW_KEY_IN_TEXT.lastIndex = sEnd;
      continue;
    }
    let depth = 0;
    let end = valueAt;
    // Quote- and escape-aware: a `}` inside a quoted value is not a structural
    // close, and treating it as one ends the subtree early — which defeats the
    // whole fail-closed policy on exactly the payloads it exists for.
    let inQuote = null;
    for (; end < value.length; end += 1) {
      const ch = value[end];
      if (inQuote) {
        if (ch === "\\") end += 1;
        else if (ch === inQuote) inQuote = null;
        continue;
      }
      if (ch === '"' || ch === "'") inQuote = ch;
      else if (ch === opener) depth += 1;
      else if (ch === closer) {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    // Unbalanced (a truncated payload) -> fail closed to the end of the string.
    const stop = depth === 0 ? end + 1 : value.length;
    out += value.slice(last, m.index) + m[1] + m[2] + REDACTED;
    last = stop;
    RAW_KEY_IN_TEXT.lastIndex = stop;
  }
  return last === 0 ? value : out + value.slice(last);
};


/**
 * An Error passed as a console argument is not a plain object, and returning it
 * verbatim bypassed every rule above it: presence producers pass Errors
 * directly (useFirebasePresence.js:51, onlineStatusManager's catch), so a
 * message, stack or own property carrying a userId, URL or presence state
 * shipped unchanged beside a message that had just been scrubbed.
 *
 * Its FIELDS are put through the normal walk; the Error INSTANCE itself is
 * preserved rather than flattened. That is deliberate and load-bearing — an
 * existing guard requires `instanceof Error`, Sentry renders an Error
 * differently from a plain object, and flattening loses the stack. An earlier
 * version of this line said the error was "reduced to a plain object", which
 * described the opposite of the code directly beneath it and would have read as
 * licence to reintroduce exactly that regression.
 */
const sanitizeError = (err, depth, isPresence) => {
  // The instance is PRESERVED — an existing guard requires `instanceof Error`,
  // because Sentry renders an Error differently from a plain object and
  // flattening it loses the stack. So the fields are scrubbed in place on a
  // copy instead: same shape to Sentry, no payload.
  const copy = new Error(scrubMessageText(String(err.message || ""), isPresence));
  copy.name = String(err.name || "Error");
  if (typeof err.stack === "string") copy.stack = scrubMessageText(err.stack, isPresence);
  // The own properties go through the OBJECT walk, not the value walk — the
  // key rules (IDENTITY_KEYS, presence vocabulary) are applied by key, so
  // scrubbing each value individually never saw the name `userId` at all.
  const props = {};
  for (const k of Object.keys(err)) props[k] = err[k];
  const scrubbed = redactStateInValue(props, depth, isPresence);
  for (const k of Object.keys(scrubbed)) copy[k] = scrubbed[k];
  return copy;
};

/**
 * #695 — non-plain class instances used to return verbatim from
 * `redactStateInValue`, so a `PresencePayload`-shaped class (or any instance
 * carrying only `userId`) bypassed presence/identity redaction. Errors keep
 * their dedicated path above. Other class instances with sensitive own keys
 * get a prototype-preserving scrub of those own properties; if the copy cannot
 * be built, fail closed to REDACTED.
 *
 * Own names come from `getOwnPropertyNames` (not `Object.keys`): a
 * non-enumerable `userId` / presence field is still an own key, and leaving the
 * instance verbatim lets a retained `toJSON` serialize the hidden value.
 * Names only — never `Object.entries` — so throwing sensitive getters stay unread.
 */
/**
 * True when `toJSON` is an own property of the value or any prototype above it.
 * Uses descriptors only — never reads `value.toJSON`, which would invoke getters.
 */
const objectOrProtoDefinesToJSON = (value) => {
  let cur = value;
  const seen = new Set();
  while (cur && typeof cur === "object" && !seen.has(cur)) {
    seen.add(cur);
    try {
      if (Object.getOwnPropertyDescriptor(cur, "toJSON")) return true;
    } catch (_ignored) {
      return true;
    }
    try {
      cur = Object.getPrototypeOf(cur);
    } catch (_ignored) {
      return true;
    }
  }
  return false;
};

const sanitizeClassInstance = (value, proto, depth, isPresence) => {
  const keyIsSensitive = (k) =>
    RAW_PAYLOAD_KEYS.has(k) ||
    IDENTITY_KEYS.includes(k) ||
    (isPresence && PRESENCE_STATE_KEYS.includes(k));
  let keys;
  try {
    keys = Object.getOwnPropertyNames(value);
  } catch (_ignored) {
    return REDACTED;
  }
  const hasSensitive = keys.some((k) => keyIsSensitive(k));
  // No sensitive own keys — leave Dates, Maps, DOM nodes, etc. alone. Nested
  // sensitive payloads on class fields without sensitive key *names* are out
  // of scope for #695 (same as the prior verbatim return).
  if (!hasSensitive) return value;
  // Inherited or own toJSON can still serialize original fields (closure or
  // `this.userId`). A prototype-preserving copy is then a leak; fail closed.
  // Detect the *property* without invoking it: an inherited accessor may return
  // undefined on the original but a serializer on the scrubbed copy (Copilot #712).
  if (objectOrProtoDefinesToJSON(value)) return REDACTED;
  try {
    const out = Object.create(proto);
    for (const k of keys) {
      let next;
      if (keyIsSensitive(k)) {
        next = REDACTED;
      } else {
        let v;
        try {
          v = value[k];
        } catch (_ignored) {
          next = REDACTED;
          v = undefined;
        }
        if (next !== REDACTED) {
          if (typeof v === "function") continue;
          next = redactStateInValue(v, depth + 1, isPresence);
        }
      }
      const desc = Object.getOwnPropertyDescriptor(value, k);
      Object.defineProperty(out, k, {
        value: next,
        enumerable: desc ? desc.enumerable : true,
        writable: true,
        configurable: true,
      });
    }
    return out;
  } catch (_ignored) {
    return REDACTED;
  }
};

const redactStateInValue = (value, depth = 0, isPresence = false) => {
  // Sentry's console integration keeps the original console arguments in
  // `data.arguments`, and they are frequently pre-formatted strings — the
  // `Status updated: isOnline=true` shape. Returning a string unchanged here
  // meant the object form was redacted and the string form, which is how these
  // are actually logged, went out intact.
  if (typeof value === "string") {
    // The full message pipeline, not a subset — an id or a URL is no less
    // identifying for arriving in data.arguments than in the message.
    return scrubMessageText(value, isPresence);
  }
  if (value === null || typeof value !== "object") return value;
  if (value instanceof Error) return sanitizeError(value, depth, isPresence);
  // Fail CLOSED at the depth ceiling. Returning the object unchanged meant a
  // sensitive key nested deeply enough bypassed this boundary entirely.
  if (depth > 6) return REDACTED;
  if (Array.isArray(value)) return value.map((v) => redactStateInValue(v, depth + 1, isPresence));
  // Rebuilding as a plain object destroys anything that is not one — an Error
  // loses its stack and its instanceof, which is the context a breadcrumb is
  // there to carry. Plain objects get walked; other class instances with
  // sensitive own keys go through sanitizeClassInstance (#695).
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return sanitizeClassInstance(value, proto, depth, isPresence);
  }
  const out = {};
  let keys;
  try {
    keys = Object.keys(value);
  } catch (_ignored) {
    return REDACTED;
  }
  for (const k of keys) {
    if (
      RAW_PAYLOAD_KEYS.has(k) ||
      IDENTITY_KEYS.includes(k) ||
      (isPresence && PRESENCE_STATE_KEYS.includes(k))
    ) {
      // The whole subtree, unread. See RAW_PAYLOAD_KEYS.
      out[k] = REDACTED;
      continue;
    }
    out[k] = redactStateInValue(value[k], depth + 1, isPresence);
  }
  return out;
};

/**
 * #528: scrub a breadcrumb at CREATION.
 *
 * Returns the breadcrumb on most normal paths, and null on FOUR, and a caller must treat null as possible.
 * This list said "the ONE exception is a heartbeat" while three of the four
 * already existed — a return contract that under-reports its own null cases is
 * how a caller comes to assume a breadcrumb always survives.
 *
 *   1. a PERIODIC presence breadcrumb — `isPeriodicPresenceBreadcrumb`, whose
 *      vocabulary is `PERIODIC_PRESENCE_ACTIONS` (heartbeat, app_foreground,
 *      presence_snapshot). Redacting `action=` leaves a timestamped breadcrumb
 *      that still says the user was online at that instant, and a 30s cadence
 *      of them reconstructs the session — disclosure by EXISTENCE, which no
 *      text filter can reach. Blanking the words does not remove the signal;
 *      only dropping the breadcrumb does.
 *   2. an automatic presence-ENDPOINT network breadcrumb —
 *      `isPresenceNetworkBreadcrumb`. Sentry instruments fetch/XHR itself, so
 *      the POST produces a breadcrumb no producer wrote and no producer can
 *      normalise; it carries the same cadence as (1).
 *   3. a presence performanceSpan reaching Session Replay — `scrubRecordingEvent`,
 *      a separate pipeline from this one, listed here because it is the same
 *      cadence leak closed in the same place.
 *   4. the catch, deliberately: if scrubbing throws, losing one breadcrumb beats
 *      letting the exception propagate into Sentry's pipeline and take the whole
 *      event with it.
 *
 * Login/logout updates are NOT dropped: they are events, not a cadence, and the
 * evidence that a presence update happened is the signal worth keeping. What
 * has to go is the state itself.
 */
/**
 * Sentry instruments fetch/XHR itself, so every presence POST produces a
 * breadcrumb we never wrote: url, method and status, and no `action` to
 * recognise it by. One arrives every 30s, so the session cadence survives
 * through a path neither producer fix reaches — the third representation of
 * this one datum, after the success log and the failure log.
 *
 * Scoped to the presence ENDPOINT rather than to fetch breadcrumbs generally:
 * dropping all of them would blind every other feature's network telemetry,
 * which is the over-redaction failure mode this filter has already hit once.
 *
 * The login/logout signal is not lost — it still arrives on the scrubbed
 * console breadcrumb, which is an event rather than a cadence.
 */
const PRESENCE_ENDPOINT = "/api/v2/user/presence";

const isPresenceNetworkBreadcrumb = (breadcrumb) => {
  // Keyed on data.url ALONE. A category check for fetch/xhr was written first
  // and a mutation proved it unreachable-as-distinct: only the http breadcrumbs
  // carry data.url, so the two branches were one. Two conditions that cannot
  // disagree are worse than one — the redundant half reads as protection and is
  // never exercised.
  return matchesPresenceEndpoint(breadcrumb.data && breadcrumb.data.url);
};

/**
 * The PERIODIC presence producers, by the action each names at SOURCE.
 *
 * Value redaction cannot remove a datum carried by a breadcrumb's EXISTENCE. A
 * fully-redacted presence line arriving every 30 seconds reconstructs the
 * client's online session precisely BECAUSE it is periodic — so these are
 * dropped rather than scrubbed.
 *
 * `heartbeat` covers `background_heartbeat` by substring, deliberately.
 *
 * `app_foreground` is here because normalising the two visibility MESSAGES was
 * not enough: the visible branch calls `updateOnlineStatus(true,
 * "app_foreground")` and the hidden branch calls nothing, so the FOLLOWING
 * breadcrumb existed in one branch and not the other. The disclosure moved one
 * breadcrumb later rather than going away.
 *
 * `presence_snapshot` is the Firebase listener. `useFirebasePresence.js`
 * subscribes to `status/${userId}` and `last_seen` sits directly under that
 * node, so every heartbeat write fires `onValue` and logs twice — at the
 * heartbeat cadence, with neither line containing the word `heartbeat`. It is
 * named at source for the same reason the failure paths were: a drop cannot
 * classify what a producer does not say.
 *
 * This is an enumeration, and an enumeration fails open: a periodic producer
 * added tomorrow without an action ships its cadence until someone notices.
 * That residual risk is real and is stated rather than papered over. What IS
 * held mechanically is the narrower thing — the markers this list depends on
 * cannot be quietly removed from the producers: a source scanner in
 * `sentryBeforeBreadcrumb.test.js` asserts both listener logs still carry
 * `action=presence_snapshot`, so deleting a marker fails the suite rather than
 * silently reopening the 30s cadence.
 */
const PERIODIC_PRESENCE_ACTIONS = [
  /heartbeat/i,
  /\bapp_foreground\b/i,
  /\bpresence_snapshot\b/i,
];

/**
 * A heartbeat breadcrumb, recognised BEFORE any redaction runs.
 *
 * Scoped to presence-marked lines so an unrelated feature's "heartbeat" (a
 * websocket keepalive diagnostic, say) keeps its telemetry.
 */
const isHeartbeatBreadcrumb = (breadcrumb, isPresence) => {
  // Scoped by the SAME decision every other path uses. It used to require a
  // marker or category of its own, so a keyed breadcrumb proven to be presence
  // by its data shape was rejected here first and kept its cadence — the
  // scoping split, one guard over.
  if (!isPresence) return false;
  const haystack = `${String(breadcrumb.message ?? "")} ${JSON.stringify(breadcrumb.data ?? "")}`;
  return PERIODIC_PRESENCE_ACTIONS.some((a) => a.test(haystack));
};

/**
 * Round 17: presence is a property of the PAYLOAD, not of the wording.
 *
 * Scoping on the literal word "presence" left a keyed logger leaking —
 * `console.log("status changed", { isOnline, platform })` carries the marker
 * nowhere, so the key list never applied and both values reached Sentry.
 *
 * Inference is by AMBIGUITY, not by count. Most of the presence vocabulary is
 * unambiguous: nothing logs `isOnline` or `batteryLevel` except presence. A
 * few names are shared with the call domain — CallV3Context deliberately logs
 * `callId` and `callType` as lifecycle context — and counting keys cannot tell
 * those two apart from presence data, since a call breadcrumb naturally
 * carries both. So a payload is presence data when it holds ANY unambiguous
 * presence key; the shared names never trigger it on their own, and stay
 * redacted inside a breadcrumb that is presence-scoped by something else.
 *
 * Erring toward redaction costs a diagnostic; erring the other way costs a
 * disclosure — but over-redaction blinded an unrelated domain once already, so
 * "always redact" is not the safe default it looks like.
 */
const AMBIGUOUS_PRESENCE_KEYS = new Set([
  // Call-domain telemetry that CallV3Context logs on purpose.
  "callId",
  "callType",
  "callDirection",
  "channel",
  "disconnectReason",
  "action",
  // Carried by almost any diagnostic.
  "timezone",
  "updated_at",
  "updatedAt",
]);

const UNAMBIGUOUS_PRESENCE_KEYS = PRESENCE_STATE_KEYS.filter(
  (k) => !AMBIGUOUS_PRESENCE_KEYS.has(k),
);

const hasUnambiguousPresenceKey = (value, depth = 0) => {
  if (!value || typeof value !== "object" || depth > 6) return false;
  let keys;
  try {
    // Same own-name surface as sanitizeClassInstance (#695 Copilot) — a
    // non-enumerable isOnline must still infer presence scope.
    keys = Object.getOwnPropertyNames(value);
  } catch (_ignored) {
    return false;
  }
  for (const k of keys) {
    if (UNAMBIGUOUS_PRESENCE_KEYS.includes(k)) return true;
    let v;
    try {
      v = value[k];
    } catch (_ignored) {
      continue;
    }
    if (hasUnambiguousPresenceKey(v, depth + 1)) return true;
  }
  return false;
};

// Shape inference for TEXT, mirroring hasUnambiguousPresenceKey for data.
// Presence is a property of the payload, not the wording — and a message is a
// payload too. Without this, `platform: "apple watch series 6"` in a message
// with no [PRESENCE] marker and no data object read as non-presence and kept
// its value, which is the regression scoping the text introduced.
const UNAMBIGUOUS_KEY_IN_TEXT = makeStateKeyMatcher(UNAMBIGUOUS_PRESENCE_KEYS);

const hasUnambiguousPresenceKeyInText = (text) => {
  if (typeof text !== "string") return false;
  UNAMBIGUOUS_KEY_IN_TEXT.lastIndex = 0;
  return UNAMBIGUOUS_KEY_IN_TEXT.test(text);
};

const isPresenceBreadcrumb = (breadcrumb, data) => {
  try {
    return (
      /presence/i.test(String(breadcrumb.message ?? "")) ||
      /presence/i.test(String(breadcrumb.category ?? "")) ||
      /presence/i.test(JSON.stringify(data ?? "")) ||
      hasUnambiguousPresenceKey(data) ||
      hasUnambiguousPresenceKeyInText(String(breadcrumb.message ?? ""))
    );
  } catch (_ignored) {
    // JSON.stringify / getters can throw; do not drop the breadcrumb.
    return (
      /presence/i.test(String(breadcrumb.message ?? "")) ||
      /presence/i.test(String(breadcrumb.category ?? "")) ||
      hasUnambiguousPresenceKey(data) ||
      hasUnambiguousPresenceKeyInText(String(breadcrumb.message ?? ""))
    );
  }
};


/**
 * #675 — callers that `console.error(err)` after an awaited api failure still
 * put response-body `err.message` on the scope via Sentry's console integration.
 * Thrown API errors carry `.status` (set in baseApi). Rewrite those console
 * breadcrumbs to status-only so body prose never rides the next capture.
 */
const scrubApiFailureConsoleBreadcrumb = (breadcrumb) => {
  if (!breadcrumb || breadcrumb.category !== "console") return breadcrumb;
  const args = breadcrumb.data && breadcrumb.data.arguments;
  if (!Array.isArray(args)) return breadcrumb;
  let status = null;
  for (const arg of args) {
    if (arg && typeof arg === "object" && typeof arg.status === "number") {
      status = arg.status;
      break;
    }
  }
  if (status === null) return breadcrumb;
  const safe = `API failure status ${status}`;
  return {
    ...breadcrumb,
    message: safe,
    data: {
      ...breadcrumb.data,
      arguments: [safe],
    },
  };
};

export function makeSentryBeforeBreadcrumb() {
  return (breadcrumb) => {
    if (!breadcrumb || typeof breadcrumb !== "object") return breadcrumb;
    try {
      breadcrumb = scrubApiFailureConsoleBreadcrumb(breadcrumb);
      // Decided ONCE, on the RAW breadcrumb, BEFORE any drop or scrub, and used
      // by every path — the drops included. Computing it later meant the
      // heartbeat guard had to re-detect presence its own way, and disagreed.
      const isPresence = isPresenceBreadcrumb(breadcrumb, breadcrumb.data);

      // Tested on the RAW message: the scrubbers below erase `action=` and the
      // direction words, so by the time it is scrubbed there is nothing left
      // to recognise a heartbeat by.
      if (isHeartbeatBreadcrumb(breadcrumb, isPresence)) return null;
      if (isPresenceNetworkBreadcrumb(breadcrumb)) return null;

      const out = { ...breadcrumb };

      if (typeof out.message === "string") {
        out.message = scrubMessageText(out.message, isPresence);
      }

      if (out.data && typeof out.data === "object") {
        // Outside presence scope only IDENTITY keys are redacted, so other
        // features keep their telemetry — in the message as well as the data.
        const data = redactStateInValue(out.data, 0, isPresence);
        if (typeof data.url === "string") data.url = scrubSensitiveUrl(data.url);
        out.data = data;
      }

      return out;
    } catch {
      // A throw here would propagate into Sentry's own pipeline. Losing the
      // breadcrumb is strictly better than losing the event it rides on.
      return null;
    }
  };
}

/**
 * The beforeSend used by src/index.js. Injectable location/UA readers keep
 * the real decision function testable (TDD rule: import the real function,
 * stub only the boundary).
 *
 * Returns the event to report it, null to drop it.
 */
export function makeSentryBeforeSend({
  getHostname = () =>
    typeof window !== "undefined" ? window.location.hostname : "",
  getPathname = () =>
    typeof window !== "undefined" ? window.location.pathname : "",
  getNavigatorUa = () =>
    typeof navigator !== "undefined" ? navigator.userAgent : "",
} = {}) {
  return function sentryBeforeSend(event, hint) {
    if (getHostname() === "localhost") return null;

    const headerUa =
      event?.request?.headers &&
      (event.request.headers["User-Agent"] ||
        event.request.headers["user-agent"]);
    const ua = headerUa || getNavigatorUa() || "";
    if (ua && SCANNER_UA_PATTERNS.some((re) => re.test(ua))) return null;

    const err = hint?.originalException;
    if (err && (err.name === "AbortError" || /AbortError/.test(String(err)))) {
      return null;
    }

    const message = extractEventMessage(event, hint);
    // Issue #628: a failure apiRequest reported centrally is NOT the fetch
    // noise #589 suppressed — it is the outage this filter was accidentally
    // hiding. #633 added the capture and it was discarded here on every route
    // but /register, so the fix was a no-op for its own target case. The
    // AbortError exclusion above still runs first and still wins: a caregiver
    // navigating away is not an outage, marked or not.
    const centrallyReported =
      event.tags && event.tags.api_reported === "true";
    if (!centrallyReported && isFetchFailureMessage(message)) {
      // beforeSend runs at SEND time; a handler that captures and then
      // immediately navigates ("/register" -> "/login") would classify the
      // event by the wrong route and drop it (review finding). A capture-time
      // tags.route set by the call site wins over the current pathname.
      // Existing call sites tag route both as "/register" and bare
      // "register" — normalize to a leading slash so the legacy shape cannot
      // defeat the very policy the hint exists to serve (review finding).
      const rawHint =
        event.tags && typeof event.tags.route === "string"
          ? event.tags.route
          : null;
      const routeHint =
        rawHint && rawHint !== ""
          ? rawHint.startsWith("/")
            ? rawHint
            : `/${rawHint}`
          : null;
      const pathname = routeHint || getPathname();
      if (!isAlwaysReportPath(pathname)) return null;
      // #589's confirming filter: the kept event is tagged with its route so
      // "register fetch failures" is a one-line Sentry search.
      event.tags = { ...event.tags, route: pathname, register_flow: "true" };
    }

    // Always last: whatever else this function decided, no event leaves with
    // a registration credential in its URLs.
    return scrubEventUrls(event);
  };
}
